import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import { useDashboard } from "../../../Hooks/storageRoles/useDashboard";
import ConsumptionSection from "../../../Components/Dashboard/ConsumptionSection";
import ConsumptionGraphSection from "../../../Components/Dashboard/ConsumptionGraphSection";
import UnfindDataWarn from "../../../Components/Utils/UnfindDataWarn";
import ActiveDevicesCard from "../../../Components/Dashboard/storageRoles/ActiveDevicesCard";
import ConsumptionBreakdownCard from "../../../Components/Dashboard/storageRoles/ConsumptionBreakdownCard";
import { groupEspressoBased } from "../../../utils/dashboardUtils";

function DashBoardPage() {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();

  // Dashboard alt izinleri
  const canSeeGraph = hasPermission("dashboard.graph");
  const canSeeDeviceStatus = hasPermission("dashboard.deviceStatus");
  const canSeeMap = hasPermission("dashboard.map");
  const canSeeBillDetails = hasPermission("dashboard.billDetails");
  const canSeeDeviceConsumption = hasPermission("dashboard.deviceConsumption");

  const {
    dashboardType,
    activeConsumption,
    isLoading,
    isFetching,
    errors,
    data,
    monthlyConsumption,
    handleClickConsumption,
    getConsumptionData,
  } = useDashboard(auth.accessToken, {
    deviceStatus: canSeeDeviceStatus,
    // firmStatus istenmez (#233): firma durumlarını yalnız kaldırılan Uyarılar
    // butonu ve Rapor Al kullanıyordu; uyarılar artık ayrı sayfada.
    firmStatus: false,
    billDetails: canSeeBillDetails,
  });


  // "Yenile" — kullanıcı isteğiyle sahte yükleme: 2sn boyunca sayfadaki TÜM
  // bölümler loading animasyonuna girer, gerçek bir istek atılmaz (veri zaten
  // socket'ten canlı akıyor)
  const [fakeRefreshing, setFakeRefreshing] = useState(false);
  const fakeRefreshTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (fakeRefreshTimerRef.current) clearTimeout(fakeRefreshTimerRef.current);
    };
  }, []);

  const handleFakeRefresh = useCallback(() => {
    setFakeRefreshing((prev) => {
      if (prev) return prev;
      fakeRefreshTimerRef.current = setTimeout(() => setFakeRefreshing(false), 2000);
      return true;
    });
  }, []);


  // Herhangi bir hata varsa
  const hasError = errors.consuments || errors.bills || errors.devicesInfo;

  const rawConsumption = getConsumptionData();
  const groupedConsumption = useMemo(
    () => (rawConsumption != null ? groupEspressoBased(rawConsumption) : null),
    [rawConsumption]
  );

  const groupedMonthlyConsumption = useMemo(() => {
    if (!Array.isArray(monthlyConsumption)) return [];

    return monthlyConsumption.map((log) => ({
      ...log,
      productConsument: groupEspressoBased(log.productConsument),
    }));
  }, [monthlyConsumption]);


  return (
    <div className="sm:py-2 sm:px-4">
      {hasError ? (
        <UnfindDataWarn Data={"Dashboard yüklenirken bir sorun oluştu !"} />
      ) : (
        <div className="flex flex-col h-[calc(100vh-64px)] sm:h-[calc(100vh-16px)]">
          {/* Scrollable Content */}
          <div
            className="flex-1 overflow-y-auto overflow-x-hidden"
            style={{ minHeight: 0 }}
          >
            <div className="space-y-3 sm:space-y-6 py-3 sm:py-4 pb-4 sm:pb-12">
              {/* Cihaz Durumları — kompakt bar, en tepede */}
              {canSeeDeviceStatus && (
                <div className="relative z-20 px-2 sm:px-0">
                  <ActiveDevicesCard
                    isRefreshing={fakeRefreshing}
                    isLoading={isLoading.devicesInfo || isFetching.devicesInfo}
                    canSeeMap={canSeeMap}
                    firmDevices={data.devicesInfo?.firmDevices}
                    testDevices={data.devicesInfo?.testDevices}
                    playmakerDevices={data.devicesInfo?.playmakerDevices}
                    serviceDevices={data.devicesInfo?.serviceDevices}
                    storageDevices={data.devicesInfo?.storageDevices}
                    productionDevices={data.devicesInfo?.productionDevices}
                    criticalDevices={data.devicesInfo?.criticalDevices}
                    unConnectedDevices={data.devicesInfo?.unConnectedDevices}
                    unUsedDevices={data.devicesInfo?.unUsedDevices}
                  />
                </div>
              )}

              <div className="relative flex flex-col lg:flex-row lg:items-stretch lg:h-[520px] xl:h-[560px] gap-3 sm:gap-4 z-10 px-2 sm:px-0">
                {/* Modern Graph Section */}
                {canSeeGraph && (
                  <ConsumptionGraphSection
                    dashboardConsumentIsLoading={isLoading.consuments || fakeRefreshing}
                    dashboardConsumentIsFetching={isFetching.consuments}
                    dashboardType={dashboardType}
                    warn={dashboardType.warn}
                    monthlyConsumption={groupedMonthlyConsumption}
                  />
                )}

                {/* Modern Consumption Section */}
                <ConsumptionSection
                  dashboardType={dashboardType}
                  activeConsumption={activeConsumption}
                  handleClickConsumption={handleClickConsumption}
                  consumption={groupedConsumption}
                  performance={data.performance}
                  warn={dashboardType.warn}
                  isLoading={isLoading.consuments || fakeRefreshing}
                  isFetching={isFetching.consuments}
                  canSeeBillDetails={canSeeBillDetails}
                  incomes={canSeeBillDetails ? data.bills?.revenue : null}
                  salesQuantity={canSeeBillDetails ? data.bills?.salesQuantity : null}
                  onRefresh={handleFakeRefresh}
                  isRefreshing={fakeRefreshing}
                />
              </div>

              {/* Cihaz + Firma tüketim kırılımları — eşit iki box */}
              {canSeeDeviceConsumption && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4 z-0 px-2 sm:px-0">
                  <ConsumptionBreakdownCard
                    mode="devices"
                    period={activeConsumption}
                    consumption={groupedConsumption}
                    performance={data.performance?.[activeConsumption]}
                    breakdown={data.consuments?.deviceBreakdown}
                    isLoading={isLoading.consuments || fakeRefreshing}
                  />
                  <ConsumptionBreakdownCard
                    mode="firms"
                    period={activeConsumption}
                    consumption={groupedConsumption}
                    performance={data.performance?.[activeConsumption]}
                    breakdown={data.consuments?.firmBreakdown}
                    isLoading={isLoading.consuments || fakeRefreshing}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DashBoardPage;
